import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState, StatusPill } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { MagnifyingGlass, PencilSimple, Plus, Trash } from "@phosphor-icons/react";

const empty = () => ({
  phone: "", first_name: "", last_name: "", email: "", list_id: "__none__", status: "NEW",
});

const LEAD_STATUSES = [
  "NEW", "QUEUE", "DIALING", "ANSWERED", "BUSY", "NOANSWER",
  "FAILED", "DNC", "COMPLETED", "CALLBACK",
];

function formatTalk(sec) {
  const s = Math.max(0, Math.floor(sec || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h) return `${h}h ${m}m ${r}s`;
  if (m) return `${m}m ${r}s`;
  return `${r}s`;
}

export default function CustomerLeads() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [listFilter, setListFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [dlg, setDlg] = useState(null);
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);

  const [activityLead, setActivityLead] = useState(null);
  const [activity, setActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);

  const loadLists = useCallback(async () => {
    try {
      const { data } = await api.get("/customer-leads/lists");
      setLists(data || []);
    } catch { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 50 };
      if (q.trim()) params.q = q.trim();
      if (listFilter !== "all") params.list_id = listFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await api.get("/customer-leads/leads", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setLoading(false);
    }
  }, [q, listFilter, statusFilter, page]);

  useEffect(() => { loadLists(); }, [loadLists]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.phone.trim()) { toast.error("Phone is required"); return; }
    setSaving(true);
    try {
      const body = {
        phone: form.phone,
        first_name: form.first_name || null,
        last_name: form.last_name || null,
        email: form.email || null,
        status: form.status,
        list_id: form.list_id === "__none__" ? null : form.list_id,
      };
      if (dlg === "new") {
        await api.post("/customer-leads/leads", body);
        toast.success("Lead added");
      } else {
        await api.patch(`/customer-leads/leads/${dlg.id}`, body);
        toast.success("Lead updated");
      }
      setDlg(null); load(); loadLists();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete lead ${row.phone}?`)) return;
    try {
      await api.delete(`/customer-leads/leads/${row.id}`);
      toast.success("Lead deleted");
      load(); loadLists();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    }
  };

  const listName = (id) => lists.find((l) => l.id === id)?.name || "—";

  const openActivity = async (lead) => {
    setActivityLead(lead);
    setActivity(null);
    setActivityLoading(true);
    try {
      const { data } = await api.get(`/customer-leads/leads/${lead.id}/activity`);
      setActivity(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
      setActivityLead(null);
    } finally {
      setActivityLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="customer-leads-page">
      <PageHeader title="Leads" subtitle="Dialable contacts shared with Lists (same underlying records)" />
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative">
            <MagnifyingGlass className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input className="pl-8 w-52" placeholder="Search…" value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} />
          </div>
          <Select value={listFilter} onValueChange={(v) => { setPage(1); setListFilter(v); }}>
            <SelectTrigger className="w-44"><SelectValue placeholder="List" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All lists</SelectItem>
              {lists.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={(v) => { setPage(1); setStatusFilter(v); }}>
            <SelectTrigger className="w-40"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button className="rounded-lg" onClick={() => { setForm(empty()); setDlg("new"); }} data-testid="lead-add">
          <Plus className="w-4 h-4 mr-1.5" /> Add lead
        </Button>
      </div>

      {loading ? <p className="text-sm text-muted-foreground">Loading…</p>
        : items.length === 0 ? <EmptyState title="No leads" description="Add leads here or import via Lists." />
          : (
            <div className="rounded-xl border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Phone</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>List</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Dial count</TableHead>
                    <TableHead>Last called</TableHead>
                    <TableHead className="w-36" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="font-mono text-sm">{r.phone}</TableCell>
                      <TableCell className="text-sm">{[r.first_name, r.last_name].filter(Boolean).join(" ") || "—"}</TableCell>
                      <TableCell className="text-xs">
                        {r.list_id ? (
                          <Link to={`/lists/${r.list_id}`} className="text-primary hover:underline">
                            {listName(r.list_id)}
                          </Link>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell><StatusPill status={r.status} /></TableCell>
                      <TableCell className="text-right tabular-nums">{r.call_count ?? 0}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {r.last_called_at ? new Date(r.last_called_at).toLocaleString() : "—"}
                      </TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button size="sm" variant="outline" className="h-8 rounded-lg" onClick={() => openActivity(r)}>
                          Activity
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => {
                          setForm({
                            phone: r.phone, first_name: r.first_name || "", last_name: r.last_name || "",
                            email: r.email || "", list_id: r.list_id || "__none__", status: r.status || "NEW",
                          });
                          setDlg(r);
                        }}><PencilSimple className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => remove(r)}><Trash className="w-4 h-4 text-destructive" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
      {total > 50 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{total} leads</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
            <Button size="sm" variant="outline" disabled={page * 50 >= total} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      )}

      <Dialog open={!!dlg} onOpenChange={(v) => !v && setDlg(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{dlg === "new" ? "Add lead" : "Edit lead"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="lead-phone" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>First name</Label>
                <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Last name</Label>
                <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Email</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>List</Label>
                <Select value={form.list_id} onValueChange={(v) => setForm({ ...form, list_id: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No list</SelectItem>
                    {lists.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setDlg(null)}>Cancel</Button>
            <Button className="rounded-lg" onClick={save} disabled={saving} data-testid="lead-save">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!activityLead} onOpenChange={(v) => !v && setActivityLead(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Lead activity{activityLead ? ` — ${activityLead.phone}` : ""}
            </DialogTitle>
          </DialogHeader>
          {activityLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {activity && (
            <div className="space-y-4 text-sm" data-testid="lead-activity-panel">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg border p-3">
                  <p className="text-[11px] text-muted-foreground">Dial count</p>
                  <p className="text-lg font-semibold tabular-nums">{activity.dial_count}</p>
                </div>
                <div className="rounded-lg border p-3 col-span-2">
                  <p className="text-[11px] text-muted-foreground">Total talk time</p>
                  <p className="text-lg font-semibold tabular-nums">{formatTalk(activity.total_talk_sec)}</p>
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">Agents who talked</p>
                {(activity.agents || []).length === 0 ? (
                  <p className="text-muted-foreground text-xs">No agent talk recorded yet.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {activity.agents.map((a) => (
                      <li key={a.user_id} className="flex justify-between gap-2 rounded-md border px-2.5 py-1.5">
                        <span>
                          <span className="font-medium">{a.name}</span>
                          {a.email && <span className="text-muted-foreground text-xs ml-1">{a.email}</span>}
                        </span>
                        <span className="text-xs tabular-nums text-muted-foreground">
                          {a.calls} call{a.calls === 1 ? "" : "s"} · {formatTalk(a.talk_sec)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">Recent calls</p>
                {(activity.recent_calls || []).length === 0 ? (
                  <p className="text-muted-foreground text-xs">No CDRs for this lead.</p>
                ) : (
                  <div className="rounded-md border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>When</TableHead>
                          <TableHead>Disposition</TableHead>
                          <TableHead className="text-right">Talk</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {activity.recent_calls.slice(0, 15).map((c) => (
                          <TableRow key={c.id}>
                            <TableCell className="text-xs">
                              {c.start_time ? new Date(c.start_time).toLocaleString() : "—"}
                            </TableCell>
                            <TableCell className="text-xs">{c.disposition || "—"}</TableCell>
                            <TableCell className="text-right text-xs tabular-nums">
                              {formatTalk(c.duration_sec)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
